import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Button, Stack, Typography } from "@mui/material";
import { CLINICAL_FONT_FAMILY } from "../../components/shared/constants";

const SPOTLIGHT_PADDING = 10;
const CARD_WIDTH = 320;
const CARD_GAP = 96;
const VIEWPORT_MARGIN = 12;

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface StudyContextCoachmarkProps {
  /** Element the spotlight should cut out and point at */
  anchorEl: HTMLElement | null;
  open: boolean;
  onDismiss: () => void;
  title?: string;
  description?: string;
}

/**
 * First-run coachmark that dims the app, cuts a spotlight around the study
 * context selector and points at it with an animated hand.
 */
export const StudyContextCoachmark: React.FC<StudyContextCoachmarkProps> = ({
  anchorEl,
  open,
  onDismiss,
  title = "Start by choosing your study",
  description = "Every dashboard, task list and report below is scoped to the study selected here. Switch studies any time from this selector.",
}) => {
  const [rect, setRect] = useState<AnchorRect | null>(null);

  const measure = useCallback(() => {
    if (!anchorEl) {
      setRect(null);
      return;
    }
    const box = anchorEl.getBoundingClientRect();
    setRect({
      top: box.top - SPOTLIGHT_PADDING,
      left: box.left - SPOTLIGHT_PADDING,
      width: box.width + SPOTLIGHT_PADDING * 2,
      height: box.height + SPOTLIGHT_PADDING * 2,
    });
  }, [anchorEl]);

  useEffect(() => {
    if (!open) {
      return;
    }
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onDismiss();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onDismiss]);

  if (!open || !rect) {
    return null;
  }

  const dimSx = {
    position: "fixed" as const,
    backgroundColor: "rgba(15, 23, 42, 0.62)",
    backdropFilter: "blur(1.5px)",
    cursor: "pointer",
  };

  const cardLeft = Math.min(
    Math.max(rect.left + rect.width / 2 - CARD_WIDTH / 2, VIEWPORT_MARGIN),
    window.innerWidth - CARD_WIDTH - VIEWPORT_MARGIN,
  );

  return createPortal(
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 20000,
        animation: "coachFadeIn 0.35s ease",
        "@keyframes coachFadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
      }}
    >
      {/* Dimming panels — the gap between them is the interactive spotlight */}
      <Box
        onClick={onDismiss}
        sx={{ ...dimSx, top: 0, left: 0, right: 0, height: rect.top }}
      />
      <Box
        onClick={onDismiss}
        sx={{
          ...dimSx,
          top: rect.top + rect.height,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />
      <Box
        onClick={onDismiss}
        sx={{
          ...dimSx,
          top: rect.top,
          left: 0,
          width: rect.left,
          height: rect.height,
        }}
      />
      <Box
        onClick={onDismiss}
        sx={{
          ...dimSx,
          top: rect.top,
          left: rect.left + rect.width,
          right: 0,
          height: rect.height,
        }}
      />

      {/* Pulsing ring around the spotlight */}
      <Box
        sx={{
          position: "fixed",
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          borderRadius: "14px",
          border: "2px solid #FBBF24",
          pointerEvents: "none",
          animation: "coachRing 1.8s ease-in-out infinite",
          "@keyframes coachRing": {
            "0%, 100%": { boxShadow: "0 0 0 0 rgba(251, 191, 36, 0.55)" },
            "50%": { boxShadow: "0 0 0 12px rgba(251, 191, 36, 0)" },
          },
        }}
      />

      {/* Pointing hand */}
      <Box
        sx={{
          position: "fixed",
          top: rect.top + rect.height + 8,
          left: rect.left + rect.width / 2 - 16,
          fontSize: "2rem",
          lineHeight: 1,
          pointerEvents: "none",
          filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.35))",
          animation: "coachHand 1.4s ease-in-out infinite",
          "@keyframes coachHand": {
            "0%, 100%": { transform: "translateY(0)" },
            "50%": { transform: "translateY(-10px)" },
          },
        }}
      >
        👆
      </Box>

      {/* Explainer card */}
      <Box
        sx={{
          position: "fixed",
          top: rect.top + rect.height + CARD_GAP,
          left: cardLeft,
          width: CARD_WIDTH,
          p: 2.25,
          borderRadius: 3,
          backgroundColor: "#FFFFFF",
          boxShadow: "0 24px 48px -12px rgba(15, 23, 42, 0.45)",
          fontFamily: CLINICAL_FONT_FAMILY,
          animation: "coachCardIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
          "@keyframes coachCardIn": {
            from: { opacity: 0, transform: "translateY(-10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
        role="dialog"
        aria-label={title}
      >
        <Typography
          sx={{
            fontFamily: CLINICAL_FONT_FAMILY,
            fontWeight: 700,
            fontSize: "1rem",
            color: "#0F172A",
            mb: 0.75,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: CLINICAL_FONT_FAMILY,
            fontSize: "0.82rem",
            color: "#475569",
            lineHeight: 1.55,
          }}
        >
          {description}
        </Typography>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="contained"
            onClick={onDismiss}
            sx={{
              fontFamily: CLINICAL_FONT_FAMILY,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 2.25,
            }}
          >
            Got it
          </Button>
        </Stack>
      </Box>
    </Box>,
    document.body,
  );
};

export default StudyContextCoachmark;
